import {convertToRoman} from 'app/helpers/convertToRoman';
import {TITLE_POSTFIX} from 'app/seo/seo.constants';
import {generateMatchDayTitle} from 'app/helpers/actionsHelpers';
import encodeHTML from 'app/helpers/encodeHTMLInStrings';

const getTeamName = team => {
  return team.level > 1 ? `${team.name.full} ${convertToRoman(team.level)}` : team.name.full;
};

const getVideoSceneTitleWithMinute = video => {
  const minute = video.additionalMinute > 0 ? `${video.minute} +${video.additionalMinute}` : video.minute;
  return `${video.title} - ${minute}. Spielminute`;
};

const getDescription = video => {
  const {competition, homeTeam, awayTeam, round} = video.match ?? {};
  const homeTeamName = homeTeam ? getTeamName(homeTeam) : '';
  const awayTeamName = awayTeam ? getTeamName(awayTeam) : '';
  let competitionName = competition?.name ?? competition?.middleName ?? competition?.shortName ?? '';
  competitionName += !competition?.active && competition?.season?.name ? ` ${competition.season.name}` : '';
  const matchDay = round ? ` - ${generateMatchDayTitle(round)}` : '';
  return `${homeTeamName} vs. ${awayTeamName}${
    competitionName ? ` - ${competitionName}` : ''
  }${matchDay} - Highlights-Clips auf FuPa.tv`;
};

const getSchema = (title, description, video) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: title,
    description: description,
    thumbnailUrl: video.thumbnail?.path ? `${video.thumbnail.path}1920x1080.jpeg` : null,
    duration: 'PT0M20S',
    uploadDate: video.dateCreated,
    contentUrl: video.source,
    provider: {
      '@type': 'Organization',
      name: 'FuPa',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.fupa.net/fupa_logo_180x60.png',
      },
    },
  };

  return encodeHTML(JSON.stringify(schema));
};

const getOgMetaData = (title, description, pathname, imagePath) => {
  let meta = [
    {property: 'og:title', content: title},
    {property: 'og:description', content: description},
    {property: 'og:url', content: `https://www.fupa.net${pathname}`},
    {property: 'og:type', content: 'video.other'},
    {property: 'og:locale', content: 'de_DE'},
    {property: 'fb:app_id', content: '939336572887264'},
  ];

  const imageSize = {height: '810', width: '1440'};
  const imageUrl = imagePath ? `${imagePath}${imageSize.width}x${imageSize.height}.jpeg` : '';

  const ogImage = imageUrl
    ? [
        {property: 'og:image', content: imageUrl},
        {property: 'og:image:secure_url', content: imageUrl},
        {property: 'og:image:type', content: 'image/jpeg'},
        {property: 'og:image:width', content: `${imageSize.width}px`},
        {property: 'og:image:height', content: `${imageSize.height}px`},
      ]
    : [];

  meta = meta.concat(ogImage);

  return meta;
};

const getMatchLineup = video => {
  const homeTeamName = video?.match?.homeTeam ? getTeamName(video.match.homeTeam) : 'Unbekanntes Team';
  const awayTeamName = video?.match?.awayTeam ? getTeamName(video.match.awayTeam) : 'Unbekanntes Team';

  return `${homeTeamName} vs. ${awayTeamName}`;
};

export const getMetaData = (playlist, videoSceneSlug, pathname) => {
  if (!playlist.length) {
    return {};
  }
  const robots = {name: 'robots', content: 'noindex, follow'};

  const videoScene = playlist.find(video => video.slug === videoSceneSlug) ?? playlist[0];
  const videoSceneTitle = getVideoSceneTitleWithMinute(videoScene);
  const matchLineup = getMatchLineup(videoScene);

  const title = videoSceneSlug ? `Video: ${videoSceneTitle} - ${matchLineup}` : `Video: ${matchLineup}`;

  const description = getDescription(videoScene);
  const ogMeta = getOgMetaData(title, description, pathname, videoScene?.thumbnail?.path);
  const schema = getSchema(videoSceneTitle, description, videoScene);

  return {title: title + TITLE_POSTFIX, meta: [...ogMeta, {name: 'description', content: description}, robots], schema};
};
